import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import {
  Plus,
  Wifi,
  X,
  Cloud,
  FolderOpen,
  Camera,
  Tag,
  Cpu,
  Loader2,
} from "lucide-react";
import {
  ModalOverlay,
  ModalShell,
  ModalHero,
  HeroGlow,
  HeroRow,
  HeroText,
  HeroBadge,
  ModalTitle,
  ModalSubtitle,
  CloseBtn,
  ModalBody,
  gradient,
} from "../../../molecules/Dashboard/EventCalendar/eventModalSharedStyles";
import {
  Field,
  FieldGrid,
  FieldFull,
  Label,
  Input,
  Select,
  ErrorSpan,
  RequiredStar,
  FormLabelIcon,
  FormGradientDefs,
} from "../../../molecules/Dashboard/Member/memberFormStyles";
import { AlbumOption, FolderOption, FormState, FormErrors } from "./types";
import {
  createC2CSession,
  C2CSessionWithFtps,
  CreateC2CSessionPayload,
} from "../../../../Redux/ApiCalls/Dashboard/C2CAPI";
import { showErrorToast } from "../../../atoms/Utlis/Toast";
import apiCall from "../../../../Redux/ApiCalls/AuthorizedApi";

interface CreateSessionModalProps {
  onClose: () => void;
  onSuccess: (session: C2CSessionWithFtps) => void;
  setC2cDisabled: (disabled: boolean) => void;
}

const ALBUMS_URL = `/project/user/projects/?page=1&page_size=100`;
const FOLDERS_URL = (albumId: number) =>
  `/project/user/get-folder/?project_id=${albumId}&fetch=4`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const FieldInput = styled(Input)<{ $hasError?: boolean }>`
  border-color: ${(p) => (p.$hasError ? "#e44862" : "rgba(23, 23, 23, 0.1)")};
  &:focus {
    border-color: ${(p) => (p.$hasError ? "#e44862" : "rgba(201, 77, 170, 0.4)")};
    box-shadow: ${(p) =>
      p.$hasError ? "0 0 0 3px rgba(228, 72, 98, 0.12)" : "0 0 0 3px rgba(201, 77, 170, 0.1)"};
  }
`;

const FieldSelect = styled(Select)<{ $hasError?: boolean }>`
  border-color: ${(p) => (p.$hasError ? "#e44862" : "rgba(23, 23, 23, 0.1)")};
  &:focus {
    border-color: ${(p) => (p.$hasError ? "#e44862" : "rgba(201, 77, 170, 0.4)")};
    box-shadow: ${(p) =>
      p.$hasError ? "0 0 0 3px rgba(228, 72, 98, 0.12)" : "0 0 0 3px rgba(201, 77, 170, 0.1)"};
  }
  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const ConnTypePill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(255, 201, 81, 0.1) 0%, rgba(206, 86, 176, 0.08) 100%);
  border: 1px solid rgba(201, 77, 170, 0.22);
  color: #c94daa;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  width: fit-content;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
  padding: 0 24px 24px;
  flex-shrink: 0;
`;

const SecondaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 110px;
  height: 46px;
  padding: 0 20px;
  border-radius: 12px;
  border: 1px solid rgba(23, 23, 23, 0.12);
  background: #fff;
  color: rgba(23, 23, 23, 0.72);
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover:not(:disabled) {
    border-color: rgba(201, 77, 170, 0.25);
    color: #c94daa;
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const PrimaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 160px;
  height: 46px;
  padding: 0 22px;
  border: none;
  border-radius: 12px;
  background: ${gradient};
  color: #fff;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(201, 77, 170, 0.25);
  transition: all 0.15s ease;
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 10px 24px rgba(201, 77, 170, 0.32);
  }
  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    transform: none;
  }
`;

const Spinner = styled(Loader2)`
  animation: ${spin} 0.8s linear infinite;
`;

export const CreateSessionModal: React.FC<CreateSessionModalProps> = ({
  onClose,
  onSuccess,
  setC2cDisabled,
}) => {
  const [albums, setAlbums] = useState<AlbumOption[]>([]);
  const [albumsLoading, setAlbumsLoading] = useState(false);
  const [folders, setFolders] = useState<FolderOption[]>([]);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [form, setForm] = useState<FormState>({
    project_id: "",
    folder_id: "",
    device_name: "",
    device_brand: "",
    device_model: "",
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadAlbums();
  }, []);

  const loadAlbums = async () => {
    setAlbumsLoading(true);
    try {
      const res = await apiCall({ method: "GET", url: ALBUMS_URL });
      const raw = res?.data ?? res;
      const list: AlbumOption[] = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.results)
        ? raw.results
        : Array.isArray(raw?.data)
        ? raw.data
        : [];
      setAlbums(list);
    } catch {
      showErrorToast("Could not load albums");
    } finally {
      setAlbumsLoading(false);
    }
  };

  const loadFolders = async (albumId: number) => {
    setFoldersLoading(true);
    setFolders([]);
    setForm((f) => ({ ...f, folder_id: "" }));
    try {
      const res = await apiCall({ method: "GET", url: FOLDERS_URL(albumId) });
      const raw = res?.data ?? res;
      const list: FolderOption[] = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.results)
        ? raw.results
        : Array.isArray(raw?.data)
        ? raw.data
        : [];
      setFolders(list);
    } catch {
      showErrorToast("Could not load folders");
    } finally {
      setFoldersLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const errors: FormErrors = {};
    if (!form.project_id) errors.project_id = "Please select an album";
    if (!form.folder_id) errors.folder_id = "Please select a folder";
    if (!form.device_name.trim()) errors.device_name = "Device name is required";
    if (!form.device_brand.trim()) errors.device_brand = "Device brand is required";
    if (!form.device_model.trim()) errors.device_model = "Device model is required";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setCreating(true);
    try {
      const payload: CreateC2CSessionPayload = {
        project_id: form.project_id as number,
        folder_id: form.folder_id as number,
        device_name: form.device_name.trim(),
        device_brand: form.device_brand.trim(),
        device_model: form.device_model.trim(),
        connection_type: "ftps",
      };
      const res = await createC2CSession(payload);
      onSuccess(res.data);
    } catch (err: any) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message ?? "";
      if (status === 404 && msg.toLowerCase().includes("album")) {
        setFormErrors((e) => ({ ...e, project_id: "Album not found or not owned by you" }));
      } else if (status === 404 && msg.toLowerCase().includes("folder")) {
        setFormErrors((e) => ({ ...e, folder_id: "Folder not found" }));
      } else if (status === 503) {
        setC2cDisabled(true);
        onClose();
      } else {
        showErrorToast(msg || "Session could not be created. Please try again.");
      }
    } finally {
      setCreating(false);
    }
  };

  return createPortal(
    <>
      <FormGradientDefs />
      <ModalOverlay
        id="c2c-create-modal-overlay"
        onClick={onClose}
      >
        <ModalShell
          style={{ maxWidth: 520 }}
          onClick={(e) => e.stopPropagation()}
        >
          <ModalHero>
            <HeroGlow />
            <HeroRow>
              <HeroText>
                <HeroBadge>
                  <Cloud size={11} />
                  Camera to Cloud
                </HeroBadge>
                <ModalTitle>New Session</ModalTitle>
                <ModalSubtitle>
                  Connect a camera device to upload photos directly to an album folder.
                </ModalSubtitle>
              </HeroText>
              <CloseBtn
                id="c2c-modal-close"
                type="button"
                onClick={onClose}
                aria-label="Close"
              >
                <X size={18} />
              </CloseBtn>
            </HeroRow>
          </ModalHero>

          <ModalBody>
            <FieldFull as="div">
              <Label htmlFor="c2c-album-select">
                <FormLabelIcon icon={FolderOpen} />
                Album
                <RequiredStar>*</RequiredStar>
              </Label>
              <FieldSelect
                id="c2c-album-select"
                value={form.project_id}
                onChange={(e) => {
                  const id = Number(e.target.value);
                  setForm((f) => ({ ...f, project_id: id }));
                  setFormErrors((er) => ({ ...er, project_id: undefined }));
                  if (id) loadFolders(id);
                }}
                $hasError={!!formErrors.project_id}
                disabled={albumsLoading}
              >
                <option value="">
                  {albumsLoading ? "Loading albums…" : "Select album"}
                </option>
                {albums.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </FieldSelect>
              {formErrors.project_id && <ErrorSpan>{formErrors.project_id}</ErrorSpan>}
            </FieldFull>

            <FieldFull as="div">
              <Label htmlFor="c2c-folder-select">
                <FormLabelIcon icon={FolderOpen} />
                Folder
                <RequiredStar>*</RequiredStar>
              </Label>
              <FieldSelect
                id="c2c-folder-select"
                value={form.folder_id}
                onChange={(e) => {
                  setForm((f) => ({ ...f, folder_id: Number(e.target.value) }));
                  setFormErrors((er) => ({ ...er, folder_id: undefined }));
                }}
                $hasError={!!formErrors.folder_id}
                disabled={!form.project_id || foldersLoading}
              >
                <option value="">
                  {foldersLoading
                    ? "Loading folders…"
                    : !form.project_id
                    ? "Select an album first"
                    : "Select folder"}
                </option>
                {folders.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </FieldSelect>
              {formErrors.folder_id && <ErrorSpan>{formErrors.folder_id}</ErrorSpan>}
            </FieldFull>

            <FieldFull as="div">
              <Label htmlFor="c2c-device-name">
                <FormLabelIcon icon={Camera} />
                Device Name
                <RequiredStar>*</RequiredStar>
              </Label>
              <FieldInput
                id="c2c-device-name"
                placeholder="e.g. Canon R5"
                maxLength={100}
                value={form.device_name}
                onChange={(e) => {
                  setForm((f) => ({ ...f, device_name: e.target.value }));
                  setFormErrors((er) => ({ ...er, device_name: undefined }));
                }}
                $hasError={!!formErrors.device_name}
              />
              {formErrors.device_name && <ErrorSpan>{formErrors.device_name}</ErrorSpan>}
            </FieldFull>

            <FieldGrid>
              <Field>
                <Label htmlFor="c2c-device-brand">
                  <FormLabelIcon icon={Tag} />
                  Brand
                  <RequiredStar>*</RequiredStar>
                </Label>
                <FieldInput
                  id="c2c-device-brand"
                  placeholder="e.g. Canon"
                  maxLength={100}
                  value={form.device_brand}
                  onChange={(e) => {
                    setForm((f) => ({ ...f, device_brand: e.target.value }));
                    setFormErrors((er) => ({ ...er, device_brand: undefined }));
                  }}
                  $hasError={!!formErrors.device_brand}
                />
                {formErrors.device_brand && <ErrorSpan>{formErrors.device_brand}</ErrorSpan>}
              </Field>
              <Field>
                <Label htmlFor="c2c-device-model">
                  <FormLabelIcon icon={Cpu} />
                  Model
                  <RequiredStar>*</RequiredStar>
                </Label>
                <FieldInput
                  id="c2c-device-model"
                  placeholder="e.g. EOS R5"
                  maxLength={100}
                  value={form.device_model}
                  onChange={(e) => {
                    setForm((f) => ({ ...f, device_model: e.target.value }));
                    setFormErrors((er) => ({ ...er, device_model: undefined }));
                  }}
                  $hasError={!!formErrors.device_model}
                />
                {formErrors.device_model && <ErrorSpan>{formErrors.device_model}</ErrorSpan>}
              </Field>
            </FieldGrid>

            <Field>
              <Label>
                <FormLabelIcon icon={Wifi} />
                Connection Type
              </Label>
              <ConnTypePill>
                <Wifi size={16} /> FTPS (Explicit TLS)
              </ConnTypePill>
            </Field>
          </ModalBody>

          <ModalFooter>
            <SecondaryBtn
              id="c2c-modal-cancel"
              type="button"
              onClick={onClose}
              disabled={creating}
            >
              Cancel
            </SecondaryBtn>
            <PrimaryBtn
              id="c2c-modal-submit"
              type="button"
              onClick={handleSubmit}
              disabled={creating}
            >
              {creating ? <Spinner size={16} /> : <Plus size={16} />}
              {creating ? "Creating…" : "Create Session"}
            </PrimaryBtn>
          </ModalFooter>
        </ModalShell>
      </ModalOverlay>
    </>,
    document.body
  );
};
